/**
 * Captura del hash de `/callback`, FUERA de React.
 *
 * Antes esto vivía dentro del render de `<Auth0Callback>`: se leía
 * `window.location.hash` y se lo borraba de la URL con `replaceState` en la
 * misma expresión de render, memorizando el valor en un `useRef`. Eso funcionó
 * mientras el componente era un import EAGER y montaba una sola vez, en el
 * primer render del árbol.
 *
 * El 17/jul/2026 (`6c1d70628`, un commit de PERFORMANCE) `/callback` pasó a
 * `React.lazy` + `<Suspense>`. Desde ese momento el componente monta tarde —
 * después de que baja su chunk— y puede montar MÁS DE UNA VEZ, porque
 * `RouterContent` desmonta `<Routes>` en dos early-returns (`!isAuth0SessionLoaded`
 * y `isAppAuthenticated && !session`). Como el borrado de la URL es un efecto
 * secundario hecho en fase de render, el primer render se llevaba el hash puesto
 * y el `useRef` del re-montaje —que es nuevo— encontraba la URL ya limpia: caía
 * en la rama "sin hash" y NUNCA corría `parseHash`/`successHandler`.
 *
 * Medido en PostHog: `auth_login_completed` pasó de 500-660/día a 30-150/día en
 * la hora exacta del deploy, y `auth_callback_no_hash` de ~20/día a 190-530/día
 * (100% con `authenticated=true` y la URL pelada). En 14 días, 2.567 de 3.504
 * sesiones con callback (73%) no ejecutaron el camino de éxito: se perdió el
 * píxel `CompleteRegistration` de Meta —su único emisor está ahí adentro—,
 * `auth_signup_completed` / `auth_login_completed`, `enforceSetupProgressOwner`
 * y el guard de account-mismatch de Google. El login no se rompía (auth0-react
 * mantiene la sesión por SSO), por eso pasó 5 semanas sin que nadie lo viera.
 *
 * La captura ahora corre una sola vez en el boot (`src/index.tsx`), antes de que
 * React monte: ningún timing de chunk, re-montaje ni render descartado puede
 * quedar en el medio. El estado vive en `window` y no en variables de módulo a
 * propósito — el entry puede evaluarse dos veces si se rompe el pinning de Skew
 * Protection (ver el guard `__flashBootstrapStarted` en index.tsx), y dos
 * instancias del módulo tendrían cada una su propia copia.
 */

declare global {
  interface Window {
    /** Hash de `/callback` capturado en el boot. `undefined` = todavía no se capturó. */
    __agoraCallbackHash?: string;
    /** Ya hubo un montaje del callback que arrancó el parse de este hash. */
    __agoraCallbackParseClaimed?: boolean;
  }
}

/** Kill-switch: `VITE_AUTH_CALLBACK_BOOT_HASH=0` vuelve al comportamiento previo. */
const BOOT_CAPTURE_ENABLED = import.meta.env.VITE_AUTH_CALLBACK_BOOT_HASH !== '0';

const isCallbackRoute = (): boolean => window.location.pathname.replace(/\/+$/, '') === '/callback';

/** Lee el hash de la URL y lo borra. Es el gesto que antes hacía el render. */
const takeHashFromLocation = (): string => {
  const hash = window.location.hash;
  if (hash) {
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  }
  return hash;
};

/**
 * Se llama UNA vez en el boot, antes de montar React. Fuera de `/callback` no
 * hace nada: en el resto de la app el hash es un ancla legítima.
 */
export const captureCallbackHashAtBoot = (): void => {
  if (!BOOT_CAPTURE_ENABLED) return;
  if (window.__agoraCallbackHash !== undefined) return;
  if (!isCallbackRoute()) return;

  window.__agoraCallbackHash = takeHashFromLocation();
};

/**
 * El hash capturado en el boot. Devuelve el MISMO valor en cada montaje del
 * callback — que es justamente el punto.
 *
 * Con el kill-switch apagado replica el comportamiento viejo exacto: leer y
 * borrar de `location` en el momento, sin memorizar nada.
 */
export const readCallbackHash = (): string => {
  if (!BOOT_CAPTURE_ENABLED) return takeHashFromLocation();
  return window.__agoraCallbackHash ?? '';
};

/**
 * Reclama el derecho a parsear el hash, una sola vez por documento.
 *
 * Es obligatorio junto con la captura durable: el `state` de auth0-js es
 * one-shot, así que si un re-montaje volviera a parsear el mismo hash el segundo
 * intento fallaría siempre con "`state` does not match" — el cluster de
 * `invalid_token` que el fix del 6/jul vino a matar. El `parsedRef` del
 * componente no alcanza porque un re-montaje lo estrena en `false`.
 *
 * @returns `true` si este montaje es el que tiene que parsear.
 */
export const claimCallbackParse = (): boolean => {
  if (window.__agoraCallbackParseClaimed) return false;
  window.__agoraCallbackParseClaimed = true;
  return true;
};
